import type { MDXContent } from 'mdx/types';

import {
  type ReleaseMeta,
  type ReleaseText,
  releaseMetaSchema,
  releaseTextSchema,
} from '../../../lib/releaseFrontmatterSchema';
import { addDays } from '../days';

// Pure logic behind the "What's new" feed: turning the fumadocs files into
// entries, deciding who sees which entry, and which ones are unread. No React,
// no Vite aliases, so `releaseEntries.node-test.ts` runs it under tsx.

export const RELEASE_LANGUAGES = [
  'en',
  'fr',
  'de',
  'it',
  'es',
  'pl',
  'pt',
] as const;

export type ReleaseLanguage = (typeof RELEASE_LANGUAGES)[number];

const isReleaseLanguage = (value: string): value is ReleaseLanguage =>
  (RELEASE_LANGUAGES as readonly string[]).includes(value);

type LoadBody = () => Promise<{ body: MDXContent }>;

// The shape fumadocs hands us for one file of the `releases` collection.
export type ReleaseFile = Record<string, unknown> & {
  info: { path: string };
  load: LoadBody;
};

type ReleaseTranslation = ReleaseText & { load: LoadBody };

export type ReleaseEntry = {
  id: string;
  meta: ReleaseMeta;
  translations: Partial<Record<ReleaseLanguage, ReleaseTranslation>> & {
    en: ReleaseTranslation;
  };
};

export type LocalizedRelease = ReleaseText & {
  id: string;
  meta: ReleaseMeta;
  load: LoadBody;
};

// `aura-ai-assistant/index.mdx` -> English, `aura-ai-assistant/index.fr.mdx`
// -> French. Anything else is not a release file.
const FILE_PATTERN =
  /^([a-z0-9]+(?:-[a-z0-9]+)*)\/index(?:\.([a-z]{2}))?\.mdx$/;

export const parseReleasePath = (
  path: string,
): { id: string; language: string } | null => {
  const match = FILE_PATTERN.exec(path.replaceAll('\\', '/'));
  if (match == null) {
    return null;
  }
  return { id: match[1], language: match[2] ?? 'en' };
};

export const buildReleaseCatalog = (
  files: readonly ReleaseFile[],
): { entries: ReleaseEntry[]; errors: string[] } => {
  const errors: string[] = [];
  const byId = new Map<
    string,
    {
      meta?: ReleaseMeta;
      translations: Partial<Record<ReleaseLanguage, ReleaseTranslation>>;
    }
  >();

  for (const file of files) {
    const parsedPath = parseReleasePath(file.info.path);
    if (parsedPath == null) {
      errors.push(`${file.info.path}: expected <id>/index[.<lang>].mdx`);
      continue;
    }
    const { id, language } = parsedPath;
    if (!isReleaseLanguage(language)) {
      errors.push(`${file.info.path}: unknown language "${language}"`);
      continue;
    }
    const text = releaseTextSchema.safeParse(file);
    if (!text.success) {
      errors.push(`${file.info.path}: ${text.error.message}`);
      continue;
    }
    const slot = byId.get(id) ?? { translations: {} };
    slot.translations[language] = { ...text.data, load: file.load };
    if (language === 'en') {
      const meta = releaseMetaSchema.safeParse(file);
      if (meta.success) {
        slot.meta = meta.data;
      } else {
        errors.push(`${file.info.path}: ${meta.error.message}`);
      }
    }
    byId.set(id, slot);
  }

  const entries: ReleaseEntry[] = [];
  for (const [id, slot] of byId) {
    const en = slot.translations.en;
    if (en == null || slot.meta == null) {
      if (en == null) {
        errors.push(`${id}: missing the English file index.mdx`);
      }
      continue;
    }
    entries.push({
      id,
      meta: slot.meta,
      translations: { ...slot.translations, en },
    });
  }

  // Newest first. Same day: `major` first, then the higher `rank`, then the
  // id so the order is stable.
  const weight = (entry: ReleaseEntry) =>
    entry.meta.importance === 'major' ? 1 : 0;
  entries.sort(
    (a, b) =>
      b.meta.date.localeCompare(a.meta.date) ||
      weight(b) - weight(a) ||
      (b.meta.rank ?? 0) - (a.meta.rank ?? 0) ||
      a.id.localeCompare(b.id),
  );
  return { entries, errors };
};

export const localizeRelease = (
  entry: ReleaseEntry,
  language: string,
): LocalizedRelease => {
  const translation =
    (isReleaseLanguage(language) ? entry.translations[language] : null) ??
    entry.translations.en;
  return { id: entry.id, meta: entry.meta, ...translation };
};

// Entries older than this are history, never "new": somebody who comes back
// after three months should not find a dozen unread items in the bell.
export const UNREAD_WINDOW_DAYS = 45;

export const isReleaseUnread = (
  meta: ReleaseMeta,
  {
    seen,
    today,
    accountCreatedDay,
  }: { seen: boolean; today: string; accountCreatedDay: string | null },
): boolean =>
  meta.announce &&
  !seen &&
  // A date still ahead (deploy preview, or a release a day late) is new.
  meta.date > addDays(today, -UNREAD_WINDOW_DAYS) &&
  // A user who joined after the release finds the feature already there.
  (accountCreatedDay == null || meta.date >= accountCreatedDay);

// An entry's `path` may point at the reader's own records: `{me}` stands for
// their user id, e.g. `/users/{me}/overview?notifications=all`.
export const resolveReleasePath = (
  path: string | undefined,
  userId: string | null | undefined,
): string | undefined => {
  if (path == null || !path.includes('{me}')) {
    return path;
  }
  return userId == null ? undefined : path.replaceAll('{me}', userId);
};
